"use client";

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import HeroSliderItem from "./HeroSliderItem";
import { SliderItemProps } from "@/data/Static/sliderdata";
import { useMotionReady } from "@/hooks/useMotionReady";

export default function HeroSlider({
  data,
  time = 6000,
}: {
  data: SliderItemProps[];
  time?: number;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const ready = useMotionReady();

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % data.length);
  }, [data.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
  }, [data.length]);

  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(nextSlide, time);
    return () => clearInterval(interval);
  }, [nextSlide, time, isHovered]);

  return (
    <div
      className="
    relative w-full overflow-hidden bg-hill-primary
    h-[100dvh]
    xl:h-[90vh]
    2xl:h-[80vh]
    min-h-[600px]
    max-h-[1000px]
  "
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={false}
          animate={ready ? { opacity: [0, 1] } : { opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <HeroSliderItem
            item={data[currentIndex]}
            isActive={true}
            isPriority={currentIndex === 0}
          />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <div className="absolute inset-x-0 bottom-20 translate-y-1/2 z-30 flex justify-between px-4 lg:px-10 pointer-events-none">
        <button
          onClick={prevSlide}
          className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-black/10 backdrop-blur-md text-white border border-white/20 hover:bg-hill-secondary hover:border-hill-secondary transition-all pointer-events-auto active:scale-95 group"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 group-hover:-translate-x-1 transition-transform" />
        </button>
        <button
          onClick={nextSlide}
          className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-black/10 backdrop-blur-md text-white border border-white/20 hover:bg-hill-secondary hover:border-hill-secondary transition-all pointer-events-auto active:scale-95 group"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6 md:w-8 md:h-8 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Pagination Dots / Progress Bar */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex items-center gap-4">
        {data.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to slide ${index + 1}`}
            className="group relative h-4 flex items-center transition-all duration-300 ease-out"
            style={{ width: currentIndex === index ? "48px" : "12px" }}
          >
            <div
              className={`absolute inset-0 rounded-full transition-all duration-500 ${
                currentIndex === index
                  ? "bg-hill-secondary"
                  : "bg-white/30 group-hover:bg-white/50"
              }`}
            />
            {currentIndex === index && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: time / 1000, ease: "linear" }}
                className="absolute inset-0 bg-white/40 rounded-full z-10"
              />
            )}
          </button>
        ))}
      </div>

      {/* Slide Counter (Subtle) */}
      <div className="absolute top-10 right-10 z-30 hidden md:block">
        <div className="text-white/40 font-karla font-bold tracking-[0.5em] text-sm flex items-baseline gap-2">
          <span className="text-white text-3xl">0{currentIndex + 1}</span>
          <span>/</span>
          <span>0{data.length}</span>
        </div>
      </div>
    </div>
  );
}
